import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { OverlayGrayBackground } from '../OverlayGrayBackground';
import { SelectDropdown } from '../SelectDropdown';
import { ConfrimModal } from '../ConfrimModal';
import { DatePickerInput } from '../DatePickerInput';
import { parsePastedDate } from '../../helpers/shipping';
import { AIR, enumBoxTypes, SEA } from '../../consts/boxTypes';
import { ShipmentsLoadingKeys } from '../../slices/shippingSlice';
import { useSelector } from 'react-redux';

export const ShippingBulkEditModal = ({
    isOpen,
    onClose,
    boxes,
    onAcceptActions,
}) => {
    const minDateBound = '2018-01-01';
    const maxDateBound = `${new Date().getFullYear() + 5}-12-31`;
    const [activeTab, setActiveTab] = useState('variants');
    const [selectedDateShipped, setSelectedDateShipped] = useState(null);
    const [selectedReceivedDate, setSelectedReceivedDate] = useState(null);
    const [trackingNumber, setTrackingNumber] = useState("");
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(null);
    const [deleteProductModalOpened, setDeleteProductModalOpened] = useState(false);

    const {
        loading,
        loadingKeys: shipmentLoadingKeys
    } = useSelector((state) => state.shipments)

    useEffect(() => {
        setTrackingNumber("");
        setSelectedShippingMethod(null);
        setSelectedReceivedDate(null);
        setSelectedDateShipped(null);
    }, [activeTab, isOpen]);

    const updateProductsIsLoading = () =>
        loading && shipmentLoadingKeys.includes(ShipmentsLoadingKeys.updateBoxes);

    const handleAcceptActions = async () => {
        await onAcceptActions?.({
            shippingMethod: selectedShippingMethod,
            trackingNumber: trackingNumber,
            receivedDate: selectedReceivedDate,
            dateShipped: selectedDateShipped
        });
    }
    const handleOnDelete = async () => {
        await onAcceptActions?.({
            toDelete: true
        });
    }

    const handleClose = () => {
        onClose();
    };

    // Handle Esc key press
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && isOpen) {
                handleClose();
            }
        };
        if (isOpen) {
            document.addEventListener('keydown', handleEscKey);
        }
        if (!isOpen) {
            setActiveTab('actions');
        }
        return () => {
            document.removeEventListener('keydown', handleEscKey);
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const modalContent = (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                <div 
                    className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
                    aria-hidden="true"
                    onClick={handleClose}
                ></div>
                <span className="hidden sm:inline-block sm:h-screen sm:align-middle" aria-hidden="true">&#8203;</span>
                <div 
                    className="inline-block transform overflow-hidden rounded-lg bg-white text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                        <div className="text-sm font-bold mb-2">
                            Selected boxes: {(boxes || []).length}   
                        </div>
                        <div className="sm:flex sm:items-start">
                            <div className="mt-3 w-full text-center sm:mt-0 sm:text-left">
                                <div className="border-b border-gray-200 mb-4">
                                    <nav className="flex -mb-px">
                                        <button
                                            onClick={() => setActiveTab('actions')}
                                            className={`ml-4 py-2 px-2 text-sm font-medium ${
                                                activeTab === 'actions'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show actions"
                                        >
                                            Actions
                                        </button>
                                        <button
                                            onClick={() => setActiveTab('delete')}
                                            className={`ml-4 py-2 px-2 text-sm font-medium ${
                                                activeTab === 'delete'
                                                ? 'border-b-2 border-blue-500 text-blue-600'
                                                : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                            } focus:outline-none`}
                                            aria-label="Show actions"
                                        >
                                            Delete
                                        </button>
                                    </nav>
                                </div>
                                {activeTab === 'actions' && (
                                    <div key={activeTab} className="flex flex-col gap-2 mb-4 relative">
                                        {(updateProductsIsLoading()) && <OverlayGrayBackground />}
                                        <div className="flex flex-col gap-1">
                                            <span>Shipping Method:</span>
                                            <div className="flex-1">
                                                <SelectDropdown
                                                    options={[
                                                        { label: "-", value: null }, 
                                                        { label: enumBoxTypes[SEA], value: SEA },
                                                        { label: enumBoxTypes[AIR], value: AIR }
                                                    ]}
                                                    value={selectedShippingMethod}
                                                    onChange={setSelectedShippingMethod}
                                                    placeholder=""
                                                    selectClassname="bg-white"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span>Tracking Number:</span>
                                            <div className="flex-1">
                                                <input
                                                    type="text"
                                                    value={trackingNumber}
                                                    onChange={(e) => {
                                                        setTrackingNumber(e.target.value)
                                                    }}
                                                    className="w-full p-2.5 shadow-sm border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <span>Date Shipped:</span>
                                            <div className="flex-1">
                                                <DatePickerInput
                                                    value={selectedDateShipped}
                                                    min={minDateBound}
                                                    max={maxDateBound}
                                                    onPaste={(e) => {
                                                        const pastedText = e.clipboardData.getData('text');
                                                        const parsed = parsePastedDate(pastedText);
                                                        if (parsed) {
                                                            e.preventDefault();
                                                            setSelectedDateShipped(parsed);
                                                        }
                                                    }}
                                                    onChange={setSelectedDateShipped}
                                                    placeholder="Date shipped"
                                                    className="w-full"
                                                    buttonClassName="!p-2.5 !text-base"
                                                />
                                            </div>
                                        </div> 
                                        <div className="flex flex-col gap-1">
                                            <span>Received Date:</span>
                                            <div className="flex-1">
                                                <DatePickerInput
                                                    value={selectedReceivedDate}
                                                    min={minDateBound}
                                                    max={maxDateBound}
                                                    onPaste={(e) => {
                                                        const pastedText = e.clipboardData.getData('text');
                                                        const parsed = parsePastedDate(pastedText);
                                                        if (parsed) {
                                                            e.preventDefault();
                                                            setSelectedReceivedDate(parsed);
                                                        }
                                                    }}
                                                    onChange={setSelectedReceivedDate}
                                                    placeholder="Received date"
                                                    className="w-full"
                                                    buttonClassName="!p-2.5 !text-base"
                                                />
                                            </div>
                                        </div>     
                                    </div>
                                )}
                                {activeTab === 'delete' && (
                                    <div key={activeTab} className="flex flex-col gap-2 mb-4 relative items-center">
                                        <button 
                                            className='inline-flex items-center w-52 justify-center px-3 py-1.5 text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 rounded border border-black disabled:opacity-50'
                                            onClick={() => setDeleteProductModalOpened(true)}
                                            disabled={updateProductsIsLoading()}
                                        >
                                            Delete Boxes
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        {activeTab !== 'delete' && (
                            <div className="py-3 sm:flex sm:flex-row-reverse">
                                {activeTab === "actions" && (
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                        onClick={handleAcceptActions}
                                    >
                                        Accept
                                    </button>
                                )}
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={handleClose}
                                >
                                    Cancel
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ConfrimModal
                isOpen={deleteProductModalOpened}
                title={"Are you sure want to delete these boxes?"}
                confirmButtonTitle={"Delete"}
                isLoading={updateProductsIsLoading()}
                submitClick={async () => {
                    setDeleteProductModalOpened(false)
                    await handleOnDelete();
                }}
                cancelClick={() => {
                    setDeleteProductModalOpened(false)
                }}
            />
        </div>
    );

    return createPortal(modalContent, document.body);
};